import { MouseEvent } from "react";
import { useLocation, Link as InternalLink } from "react-router-dom";
import styled from "styled-components";

interface NavItemType {
  label: string;
  home?: boolean;
  /** route path without leading slash, e.g. gallery */
  component?: string;
}

const navItems: NavItemType[] = [
  {
    label: "About",
    home: true,
  },
  {
    label: "Gallery",
    component: "gallery",
  },
  {
    label: "License",
    component: "license",
  },
  {
    label: "Play Cyber Run",
    component: "cyber-run",
  },
];

const StyledNavItems = styled.nav`
  display: flex;
  gap: 2rem;

  @media only screen and (max-width: 750px) {
    flex-direction: column;
    align-items: center;
    gap: 15px;
  }

  @media only screen and (max-width: 1307px) {
    gap: 2rem;
  }

  a:focus-visible {
    outline: 2px solid var(--primary);
    outline-offset: 3px;
  }
`;

const NavItem = styled.div<{ $activeTab: boolean }>`
  text-transform: uppercase;
  color: ${({ $activeTab }) => ($activeTab ? "var(--primary)" : "var(--sub)")};
  cursor: pointer;
  font-family: "Space Grotesk", sans-serif;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.05rem;

  @media only screen and (max-width: 1307px) {
    font-size: 1.9rem;
  }

  transition: all 0.3s;
  :hover {
    color: ${({ $activeTab }) => ($activeTab ? "var(--primary)" : "var(--main)")};
    transform: translateY(-2px);
    font-weight: bold;
  }
`;

interface NavItemsProps {
  onNavigate?: () => void;
}

const NavItems = ({ onNavigate }: NavItemsProps) => {
  const location = useLocation();

  const isHome = location.pathname === "/";
  const scrollToHomeTop = (event: MouseEvent<HTMLAnchorElement>) => {
    onNavigate?.();
    if (!isHome) return;

    event.preventDefault();
    // Desktop home scrolls an inner snap container; mobile scrolls the window.
    const homeScrollRoot = document.getElementById("home-scroll-root");
    if (homeScrollRoot) homeScrollRoot.scrollTop = 0;
    window.scrollTo(0, 0);
  };

  return (
    <StyledNavItems aria-label="Site">
      {navItems.map((navItem: NavItemType) => (
        <InternalLink
          to={navItem.home ? "/" : `/${navItem.component}`}
          key={navItem.label}
          onClick={navItem.home ? scrollToHomeTop : onNavigate}
          aria-current={
            !navItem.home && location.pathname === `/${navItem.component}`
              ? "page"
              : undefined
          }
        >
          <NavItem
            $activeTab={
              !navItem.home && location.pathname === `/${navItem.component}`
            }
          >
            {navItem.label}
          </NavItem>
        </InternalLink>
      ))}
    </StyledNavItems>
  );
};

export default NavItems;
